import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import clsx from 'clsx'

import { useOutsideClick } from '@/common/util/outside'
import { ChildProps } from '@/common/util/props'
import { getRect, sumAllParentScrollLeftTop, useRect } from '@/common/util/rect'

import Disclosure, {
  DisclosureGap,
  DisclosurePositionHorizon,
  DisclosurePositionVertical,
  DisclosureSize
} from '@/components/view/layouts/disclosure/Disclosure'
import { useDisclosure } from '@/components/view/utils/disclousure/DisclosureProvider'

interface CommonProps {
  disclosureNode: ReactNode
  gap?: DisclosureGap
  isForm?: boolean
  size?: DisclosureSize
  widthFull?: boolean
  onClickOutSide?: (e: MouseEvent) => void
}

interface Props extends ChildProps, CommonProps {
  remnant?: boolean
  off: boolean
  horizon?: DisclosurePositionHorizon
  vertical?: DisclosurePositionVertical
}

interface BodyMountingDisclosureProps extends CommonProps {
  wrapRef: React.RefObject<HTMLDivElement | null>
}

// Basically show the disclosure at bottom right of the target
// Show at left if can not be shown in the window horizontally
// Show at top if can not be shown in the window vertically
const calculatePositionOnWindow = (
  wrapRef: React.RefObject<HTMLDivElement | null>,
  disclosureInnerRect: DOMRect
) => {
  const wrapRect = getRect(wrapRef)
  if (!wrapRect) {
    return null
  }
  const windowWidth = window.innerWidth
  const windowHeight = window.innerHeight

  const { left: accumulatedScrollLeft, top: accumulatedScrollTop } =
    sumAllParentScrollLeftTop(wrapRef)
  const scrollParentRect = wrapRef.current?.closest('[style*="overflow"]')?.getBoundingClientRect()

  const { parentBottom, parentLeft } = {
    parentBottom: scrollParentRect ? scrollParentRect.bottom : windowHeight,
    parentLeft: scrollParentRect ? scrollParentRect.left : windowWidth
  }

  const { shouldShowAbove, shouldShowRight } = {
    shouldShowAbove: wrapRect.bottom > parentBottom - disclosureInnerRect.height,
    shouldShowRight: wrapRect.left > parentLeft - disclosureInnerRect.width
  }

  const { left, right, top, bottom } = {
    left: shouldShowRight ? undefined : wrapRect.left + window.scrollX,
    right: shouldShowRight
      ? windowWidth - wrapRect.right - window.scrollX + disclosureInnerRect.width
      : undefined,
    top: shouldShowAbove ? undefined : wrapRect.bottom + window.scrollY - accumulatedScrollTop,
    bottom: shouldShowAbove
      ? windowHeight -
        wrapRect.top -
        window.scrollY +
        accumulatedScrollTop +
        disclosureInnerRect.height
      : undefined
  }

  return {
    left: left ? `${left}px` : undefined,
    right: right ? `${right}px` : undefined,
    top: top ? `${top}px` : undefined,
    bottom: bottom ? `${bottom}px` : undefined
  }
}

const BodyMountingDisclosure = ({
  disclosureNode,
  gap,
  isForm,
  size = DisclosureSize.DEFAULT,
  widthFull,
  wrapRef,
  onClickOutSide
}: BodyMountingDisclosureProps) => {
  const disclosureOriginRef = useOutsideClick<HTMLDivElement>(onClickOutSide)
  const [disclosureRef, disclosureRect] = useRect(disclosureOriginRef)
  const { setShowing } = useDisclosure()

  const [position, setPosition] = useState<{
    left: string | undefined
    right: string | undefined
    top: string | undefined
    bottom: string | undefined
  } | null>(null)

  const open = useCallback(() => {
    if (disclosureRect) {
      setPosition(calculatePositionOnWindow(wrapRef, disclosureRect))
    }
    document.body.style.overflow = 'hidden'
  }, [disclosureRect])

  const close = useCallback(() => {
    document.body.style.overflow = ''
  }, [])

  useEffect(() => {
    open()
    setShowing(true)
    return () => {
      close()
      setShowing(false)
    }
  }, [disclosureRect, setShowing])

  return (
    <>
      {createPortal(
        <div
          className={clsx('z-disclosure fixed', position && 'pointer-events-auto')}
          style={position ?? {}}
        >
          <Disclosure
            ref={disclosureRef}
            gap={gap}
            isForm={isForm}
            size={widthFull ? DisclosureSize.FULL : size}
          >
            {disclosureNode}
          </Disclosure>
        </div>,
        document.body
      )}
    </>
  )
}

export const makeWrapperClass = ({
  remnant,
  widthFull
}: {
  remnant?: boolean
  widthFull?: boolean
}) => {
  return clsx('relative', widthFull && 'w-full max-w-full', remnant && 'flex-1 min-w-0')
}

const BodyMountingDisclosureWrapper = (props: Props) => {
  const { children, remnant, widthFull, off, ...otherProps } = props
  const wrapRef = useRef<HTMLDivElement | null>(null)

  return (
    <div ref={wrapRef} className={makeWrapperClass({ remnant, widthFull })}>
      {children}
      <>{!off && <BodyMountingDisclosure {...otherProps} wrapRef={wrapRef} />}</>
    </div>
  )
}

const NearOriginDisclosureWrapper = (props: Props) => {
  const { children, remnant, widthFull, off, size, ...otherProps } = props
  const wrapRef = useOutsideClick<HTMLDivElement>(otherProps.onClickOutSide, !off)

  return (
    <div ref={wrapRef} className={makeWrapperClass({ remnant, widthFull })}>
      {children}
      <>
        {!off && (
          <>
            <Disclosure
              gap={otherProps.gap}
              isForm={otherProps.isForm}
              horizon={otherProps.horizon}
              vertical={otherProps.vertical}
              size={size ?? (widthFull ? DisclosureSize.FULL : DisclosureSize.DEFAULT)}
            >
              {otherProps.disclosureNode}
            </Disclosure>
          </>
        )}
      </>
    </div>
  )
}

const DisclosureWrapper = (props: Props) => {
  const { children, ...otherProps } = props
  const bodyMounting = otherProps.horizon == undefined && otherProps.vertical == undefined

  return bodyMounting ? (
    <BodyMountingDisclosureWrapper {...props} />
  ) : (
    <NearOriginDisclosureWrapper {...props} />
  )
}

export default DisclosureWrapper
